type EngineeringTeam = {
  name: string;
  key: string;
  datadog: {
    teamTag: string;
  };
  playwright: {
    tag: string;
  };
  slack: {
    userGroupId: string;
    userGroupHandle: string;
  };
};

/** Registry of engineering teams and their identifiers across systems. */
export const engineeringTeams = {
  /**
   * All engineers on Team AI Configs
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C07GGGLCGQG | #team-ai-configs} */
  TEAM_AI_CONFIGS: {
    name: 'Team AI Configs',
    key: 'team-ai-configs',
    datadog: {
      teamTag: 'team-ai-configs',
    },
    playwright: {
      tag: '@team-ai-configs',
    },
    slack: {
      userGroupId: 'S07MDS9MDT3',
      userGroupHandle: '@ai-team',
    },
  },
  /**
   * All engineers on Team Billing
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/CUEN57YTA | #ask-billing-and-internal-systems} */
  TEAM_BILLING: {
    name: 'Team Billing',
    key: 'team-billing',
    datadog: {
      teamTag: 'team-billing',
    },
    playwright: {
      tag: '@team-billing',
    },
    slack: {
      userGroupId: 'S07QVPCS21X',
      userGroupHandle: '@billing-internal-systems-team',
    },
  },
  /**
   * All engineers on Team Enterprise
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C09N9RR6A7N | #team-enterprise} */
  TEAM_ENTERPRISE: {
    name: 'Team Enterprise',
    key: 'team-enterprise',
    datadog: {
      teamTag: 'team-fm-enterprise',
    },
    playwright: {
      tag: '@team-enterprise',
    },
    slack: {
      userGroupId: 'S0AHTT4DMLZ',
      userGroupHandle: '@team-enterprise-engineers',
    },
  },
  /**
   * All engineers on Team Experimentation
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C02MDJRGLMU | #team-experimentation} */
  TEAM_EXPERIMENTATION: {
    name: 'Team Experimentation',
    key: 'team-experimentation',
    datadog: {
      teamTag: 'team-experimentation',
    },
    playwright: {
      tag: '@team-experimentation',
    },
    slack: {
      userGroupId: 'S06UR1TDU3T',
      userGroupHandle: '@experimentation-oncall',
    },
  },
  /**
   * All engineers on Team Foundation
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C0ACDTU2E4D | #team-foundation} */
  TEAM_FOUNDATION: {
    name: 'Team Foundation',
    key: 'team-foundation',
    datadog: {
      teamTag: 'team-foundation',
    },
    playwright: {
      tag: '@team-foundation',
    },
    slack: {
      userGroupId: 'S0AHTSQJVMF',
      userGroupHandle: '@team-foundation-engineers',
    },
  },
  /**
   * All engineers on Team Metrics
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C0782T6HE3Z | #team-metrics} */
  TEAM_METRICS: {
    name: 'Team Metrics',
    key: 'team-metrics',
    datadog: {
      teamTag: 'team-metrics',
    },
    playwright: {
      tag: '@team-metrics',
    },
    slack: {
      userGroupId: 'S095RCTN0F4',
      userGroupHandle: '@metrics-oncall',
    },
  },
  /**
   * All engineers on Team Release Guardian
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C012PMFLK8T | #team-release-guardian} */
  TEAM_RELEASE_GUARDIAN: {
    name: 'Team Release Guardian',
    key: 'team-release-guardian',
    datadog: {
      teamTag: 'team-release-guardian',
    },
    playwright: {
      tag: '@team-release-guardian',
    },
    slack: {
      userGroupId: 'S08AXCU16BT',
      userGroupHandle: '@rg-on-call-engineer',
    },
  },
  /**
   * All engineers on Team Observability
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/S08M6CK16RZ | #o11y-team} */
  TEAM_O11Y: {
    name: 'Team Observability',
    key: 'team-o11y',
    datadog: {
      teamTag: 'team-o11y',
    },
    playwright: {
      tag: '@team-o11y',
    },
    slack: {
      userGroupId: 'S08M6CK16RZ',
      userGroupHandle: '@o11y-team',
    },
  },
} as const satisfies Record<`TEAM_${string}`, EngineeringTeam>;

export type EngineeringTeamKey = (typeof engineeringTeams)[keyof typeof engineeringTeams]['key'];
